import type { MultiPolygon as ClippingMultiPolygon } from 'martinez-polygon-clipping';
import type { BuildingDesign } from '@shared/models/design/building-design.model';
import type { Storey } from '@shared/models/design/storey.model';
import type { ProjectMapPreview } from '@shared/models/design/project-map-preview.model';
import type { Point2D } from '@shared/models/geometry.model';
import { getShapeRingCoordinates } from './shape.helpers';
import {
  computePolygonArea,
  isPolygon2D,
  isMartinezPolygon,
} from '../geometry/geometry.helpers';
import {
  MARTINEZ_COORD_DECIMALS,
  type MartinezClipOp,
} from '../geometry/martinez-polygon-clipping.helpers';
import { roundToDecimals } from '../math.helpers';

/**
 * Martinez union for footprint merging.
 * Pass an ESM-loaded op from migrations; app code passes `safeMartinezUnion`
 * from `martinez-polygon-clipping-ops.helpers`.
 */
export type PolygonUnion = MartinezClipOp;

/**
 * Stable key so storeys that only differ by float noise count as one footprint.
 */
const footprintDedupeKey = (points: readonly Point2D[]): string =>
  JSON.stringify(
    points.map(([x, y]): [number, number] => [
      roundToDecimals(x, MARTINEZ_COORD_DECIMALS),
      roundToDecimals(y, MARTINEZ_COORD_DECIMALS),
    ]),
  );

/**
 * Top-down equivalent of Design's volume silhouette: union solid storey shapes,
 * skipping mezzanines and cutouts. Repeated floors contribute only once, and
 * interior openings are omitted just as in the volume mask.
 */
export const getProjectMapPreview = (
  design:
    | BuildingDesign
    | (Omit<BuildingDesign, 'buildings'> & { storeys: Storey[] })
    | undefined,
  /** Required so this module never statically imports package CJS Martinez. */
  unionPolygons: PolygonUnion,
): ProjectMapPreview | undefined => {
  if (
    !design?.location ||
    !Number.isFinite(design.location.lat) ||
    !Number.isFinite(design.location.lng)
  )
    return undefined;
  const unionFn = unionPolygons;
  const buildings =
    'buildings' in design
      ? design.buildings
      : [{ id: 'legacy-building', storeys: design.storeys }];
  return {
    location: design.location,
    outlines: {
      type: 'FeatureCollection',
      features: buildings.flatMap((building) => {
        const seen = new Set<string>();
        let polygons: ClippingMultiPolygon = [];
        for (const storey of building.storeys) {
          if (storey.mezzanine) continue;
          for (const shape of storey.shapes ?? []) {
            if (shape.cutout || !isPolygon2D(shape.points)) continue;
            if (
              !shape.points.every(
                ([x, y]) => Number.isFinite(x) && Number.isFinite(y),
              )
            )
              continue;
            if (computePolygonArea(shape.points) === 0) continue;
            const key = footprintDedupeKey(shape.points);
            if (seen.has(key)) continue;
            seen.add(key);
            const ring = shape.points.map(([x, y]): [number, number] => [x, y]);
            const first = ring[0];
            const last = ring[ring.length - 1];
            if (first && (first[0] !== last?.[0] || first[1] !== last[1]))
              ring.push(first);
            // Injected safe union snaps + short-circuits float-noise hangs.
            const merged = polygons.length
              ? unionFn(polygons, [ring])
              : [[ring]];
            if (merged)
              polygons = isMartinezPolygon(merged) ? [merged] : merged;
          }
        }
        // Only the outer boundary of each disconnected part is a silhouette.
        const coordinates = polygons.flatMap((polygon) => {
          const points = polygon[0];
          return points && isPolygon2D(points)
            ? [
                [
                  getShapeRingCoordinates(
                    { id: building.id, points },
                    design.location,
                  ),
                ],
              ]
            : [];
        });
        return coordinates.length
          ? [
              {
                type: 'Feature' as const,
                properties: { buildingId: building.id },
                geometry: { type: 'MultiPolygon' as const, coordinates },
              },
            ]
          : [];
      }),
    },
  };
};
